import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShoppingBag, Lock } from "lucide-react";
import api from "../lib/api";
import { CHAPTERS } from "../data/content";
import { UpgradeModal } from "../components/UpgradeModal";
import { FoundingBadge } from "../components/FoundingBadge";

function PhotoImg({ path, className = "" }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        u = URL.createObjectURL(res.data);
        setUrl(u);
      } catch (e) {}
    })();
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  if (!url) return null;
  return <img src={url} alt="" className={className} style={{ borderRadius: 2 }} />;
}

export default function BookPreview() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [family, setFamily] = useState(null);
  const [stories, setStories] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [plist, fam, slist, meRes] = await Promise.all([
          api.get("/profiles"),
          api.get("/families/me"),
          api.get(`/stories/${profileId}`),
          api.get("/auth/me"),
        ]);
        const m = (plist.data || []).find((p) => p.profile_id === profileId);
        if (!m) { navigate("/dashboard"); return; }
        setProfile(m); setFamily(fam.data); setStories(slist.data || []); setMe(meRes.data);
      } catch (e) { navigate("/dashboard"); } finally { setLoading(false); }
    })();
  }, [profileId, navigate]);

  const pages = useMemo(() => {
    if (!profile || !family) return [];
    const filled = stories.filter((s) => (s.answer_text || "").trim().length > 0);
    const ordered = [...filled].sort((a, b) => a.chapter_id - b.chapter_id || a.prompt_index - b.prompt_index);
    const built = [{ kind: "cover" }];
    let lastChapter = null;
    for (const s of ordered) {
      if (s.chapter_id !== lastChapter) {
        built.push({ kind: "chapter-title", chapter_id: s.chapter_id });
        lastChapter = s.chapter_id;
      }
      built.push({ kind: "story", story: s });
    }
    if (ordered.length === 0) built.push({ kind: "empty" });
    return built;
  }, [profile, family, stories]);

  const total = pages.length;
  const isFree = (me?.subscription_tier || "free") === "free";
  const freeLimit = 3; // free users see first 3 pages
  const go = (n) => {
    if (n < 0 || n >= total) return;
    if (isFree && n >= freeLimit) {
      setUpgradeOpen(true);
      return;
    }
    setFlipping(true);
    setTimeout(() => { setPage(n); setFlipping(false); }, 320);
  };

  if (loading || !profile) return (
    <div className="min-h-screen flex items-center justify-center paper">
      <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>Reflecting on your story…</p>
    </div>
  );

  const current = pages[page];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid="book-preview-page">
      <header className="max-w-5xl mx-auto w-full px-6 sm:px-10 py-6 flex items-center justify-between">
        <Link to={`/app/profile/${profileId}`} className="flex items-center gap-2 label-ui text-[11px] text-[#E8D5A3] hover:text-white transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> Back
        </Link>
        <button onClick={() => navigate(`/app/order/${profileId}`)} className="btn-gold flex items-center gap-2" data-testid="order-from-preview">
          <ShoppingBag size={16} /> Order this book
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-2xl">
          {/* Book page */}
          <div
            className={`bg-[#FAF7F2] mx-auto transition-all duration-500 ${flipping ? "opacity-0 -translate-x-2" : "opacity-100 translate-x-0"} relative`}
            style={{
              aspectRatio: "3/4",
              maxWidth: 560,
              width: "100%",
              boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(184,151,58,0.18)",
              borderRadius: 4,
            }}
            data-testid="book-page"
          >
            <BookPage page={current} pageNumber={page} profile={profile} family={family} totalStories={stories.length} isFoundingMember={!!me?.is_founding_member} />
            {isFree && (
              <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                <p className="label-ui text-[9px] text-[#B8973A]/40 mb-3 tracking-[0.3em]">SUBSCRIBE TO UNLOCK YOUR FULL BOOK</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10">
            <button onClick={() => go(page - 1)} disabled={page === 0} className="btn-outline-cream disabled:opacity-30 flex items-center gap-2" data-testid="prev-page-btn">
              <ChevronLeft size={16} /> Previous
            </button>
            <div className="flex items-center gap-2" data-testid="page-dots">
              {Array.from({ length: Math.min(total, 12) }).map((_, i) => {
                const idx = Math.floor((i / Math.min(total, 12)) * total);
                const active = idx === page;
                return (
                  <button
                    key={i}
                    onClick={() => go(idx)}
                    aria-label={`Go to page ${idx + 1}`}
                    style={{ width: 8, height: 8, borderRadius: 999, background: active ? "#B8973A" : "rgba(232,213,163,0.35)" }}
                  />
                );
              })}
            </div>
            {isFree && page + 1 >= freeLimit ? (
              <button onClick={() => setUpgradeOpen(true)} className="btn-gold flex items-center gap-2" data-testid="unlock-pages-btn">
                <Lock size={14} /> Unlock more
              </button>
            ) : (
              <button onClick={() => go(page + 1)} disabled={page >= total - 1} className="btn-outline-cream disabled:opacity-30 flex items-center gap-2" data-testid="next-page-btn">
                Next <ChevronRight size={16} />
              </button>
            )}
          </div>

          <p className="text-center text-[#E8D5A3] italic mt-8" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {isFree ? `Page ${page + 1} of ${freeLimit} (preview) · ${total} pages total` : `Page ${page + 1} of ${total}`}
          </p>
          {isFree && (
            <p className="text-center text-[#E8D5A3]/70 italic mt-3 text-sm" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Your book is taking shape beautifully. <button onClick={() => setUpgradeOpen(true)} className="text-[#B8973A] underline-offset-4 hover:underline" data-testid="preview-upgrade-link">Subscribe to preview every page.</button>
            </p>
          )}
        </div>
      </main>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="book_preview" />
    </div>
  );
}

function BookPage({ page, pageNumber, profile, family, totalStories, isFoundingMember }) {
  if (!page) return null;
  if (page.kind === "cover") {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center px-10" style={{ background: "#111D33", borderRadius: 4 }}>
        <div className="hairline w-full h-full flex flex-col items-center justify-center" style={{ borderColor: "rgba(184,151,58,0.6)", padding: "10%" }}>
          <p className="text-[#B8973A] text-3xl mb-8" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</p>
          <p className="label-ui text-[11px] text-[#B8973A] mb-6">Volume I</p>
          <h2 className="text-4xl sm:text-5xl text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", lineHeight: 1.15 }}>
            {family?.name || "Our Family"}
          </h2>
          <div className="my-8 w-14 h-px" style={{ background: "#B8973A" }} />
          <p className="text-[#E8D5A3]" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "1.1rem" }}>
            The stories of {profile.name}
          </p>
          <p className="text-[#E8D5A3] mt-2" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem" }}>
            {totalStories} stor{totalStories === 1 ? "y" : "ies"} captured
          </p>
          {isFoundingMember && (
            <div className="mt-6"><FoundingBadge active size="md" /></div>
          )}
          <p className="mt-6 text-[#E8D5A3] italic max-w-xs mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem", lineHeight: 1.5 }} data-testid="cover-manifesto">
            We build for grandchildren, not algorithms.
          </p>
          <div className="absolute bottom-8 left-0 right-0">
            <p className="label-ui text-[9px] text-[#B8973A] text-center">A Living Book</p>
          </div>
        </div>
      </div>
    );
  }
  if (page.kind === "empty") {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center p-12">
        <p className="text-[#B8973A] text-2xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</p>
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.6 }}>
          The first page of {profile.name}'s book is waiting for the first story.
        </p>
      </div>
    );
  }
  if (page.kind === "chapter-title") {
    const ch = CHAPTERS.find((c) => c.id === page.chapter_id);
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center px-12">
        <p className="chapter-roman text-base" data-testid="book-chapter-roman">{ch.roman}</p>
        <h2 className="text-4xl sm:text-5xl mt-4" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400 }}>
          {ch.title}
        </h2>
        <div className="my-6 w-12 h-px" style={{ background: "#B8973A" }} />
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
          {ch.subtitle}
        </p>
      </div>
    );
  }
  // story page
  const s = page.story;
  const ch = CHAPTERS.find((c) => c.id === s.chapter_id);
  return (
    <div className="w-full h-full p-10 flex flex-col">
      <p className="chapter-roman text-[10px] mb-4">{ch.roman} · {ch.title}</p>
      {s.photo_path && (
        <div className="mb-5 -mx-4">
          <PhotoImg path={s.photo_path} className="w-full max-h-32 object-cover" />
        </div>
      )}
      <p className="prompt-italic text-sm mb-4">"{ch.prompts[s.prompt_index]}"</p>
      <p className="story-text text-[0.9rem] flex-1 overflow-hidden line-clamp-[14] whitespace-pre-wrap">
        {s.answer_text}
      </p>
      <div className="mt-auto pt-5 border-t hairline-cream flex items-center justify-between">
        <p className="label-ui text-[9px] text-[#B8973A]">— {profile.name}</p>
        <p className="label-ui text-[9px] text-[#8C8278]">{pageNumber + 1}</p>
      </div>
    </div>
  );
}
